import { Box, Tab, Tabs } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EvalCalibrationPage from './EvalCalibrationPage';
import EvalFixturesPage from './EvalFixturesPage';
import EvalReviewQueuePage from './EvalReviewQueuePage';
import EvalRunsPage from './EvalRunsPage';
import EvalsPage from './EvalsPage';

const TAB_KEYS = ['workbench', 'fixtures', 'runs', 'review', 'calibration'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TAB_LABELS: Record<TabKey, string> = {
  workbench: 'Suites & Cases',
  fixtures: 'Fixture Sets',
  runs: 'Run History',
  review: 'Review Queue',
  calibration: 'Calibration',
};

const TAB_TOOLTIPS: Record<TabKey, string> = {
  workbench: 'Create and manage eval suites and their test cases',
  fixtures: 'Create and version transcript-backed fixture sets',
  runs: 'View past eval runs and filter by status or suite',
  review: 'Runs flagged for human review or detected as regressions',
  calibration: 'Track how well the AI judge agrees with human reviewers',
};

function isValidTab(value: string | null): value is TabKey {
  return value !== null && TAB_KEYS.includes(value as TabKey);
}

export default function EvalsDashboard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeTab: TabKey = useMemo(() => {
    const param = searchParams.get('tab');
    return isValidTab(param) ? param : 'workbench';
  }, [searchParams]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: TabKey) => {
      const params = new URLSearchParams(searchParams);
      if (newValue === 'workbench') {
        params.delete('tab');
      } else {
        params.set('tab', newValue);
      }
      navigate({ pathname: '/admin/evals', search: params.toString() }, { replace: true });
    },
    [navigate, searchParams]
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="standard"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              minHeight: 48,
            },
          }}
        >
          {TAB_KEYS.map((key) => (
            <Tab key={key} value={key} label={TAB_LABELS[key]} title={TAB_TOOLTIPS[key]} />
          ))}
        </Tabs>
      </Box>

      {activeTab === 'workbench' && <EvalsPage />}
      {activeTab === 'fixtures' && <EvalFixturesPage />}
      {activeTab === 'runs' && <EvalRunsPage />}
      {activeTab === 'review' && <EvalReviewQueuePage />}
      {activeTab === 'calibration' && <EvalCalibrationPage />}
    </Box>
  );
}
